/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  LuiEmptyState,
  LuiMaterialTable,
  LuiPageHeader,
  LuiToolbar,
  LuiMaterialTableHeadCellStyles
} from '@jda/lui-common-component-library-mui5';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  FormGroup,
  FormLabel,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
  TableCellProps
} from '@mui/material';
import CustomTablePagination from '../../shared/CustomTablePagination';
import DetailsModal from '../../shared/Dialog/DetailsModal';
import SnackbarAlert from '../../shared/SnackbarAlert';
import {
  getClientById,
  getModuleTopics,
  addMachineTopicSubscriptions,
  updateMachineTopicSubscriptions,
  getTopicSubscriptions
} from '../../services/client';
import { setSelectedTab } from '../../store/appLoader/appLoaderActionCreator';
import {
  setCurrentMachineName,
  setCurrentMachineSelection,
  setMachineSelectedTab
} from '../../store/machine/machineActionCreator';
import { displaySnackbar } from '../../store/snackBar/snackBarActionCreator';
import useWindowDimensions from '../../utility/useWindowDimensions';
import { IClient } from '../../models/Machines/ClientContracts';
import interpolateString from '../../utility/stringInterpolation';
import { useStyles } from './styles';
import {
  ADD_SUBSCRIPTIONS,
  EMPTY_HEADER,
  NAME,
  ACCESS,
  ROWS_PER_PAGE_LABEL,
  UNSUCCESSFUL_SUBSCRIPTION_TITLE,
  UNSUCCESSFUL_SUBSCRIPTION_MESSAGE,
  ERROR_MACHINE_NOT_FOUND,
  TOPICS_SUBSCRIBED_SUCCESSFULLY,
  ERROR_WHILE_FETCHING_DATA,
  SUBSCRIBE,
  TOPIC_SUBSCRIBE_WARN,
  UNSUCCESSFUL_TOPIC_SUBSCRIBE_MESSAGE,
  PRODUCER,
  CONSUMER,
  ACCESS_LABELS
} from './constants';
import { ERROR_WHILE_FETCHING_SUBSCRIPTIONS, LABELS } from '../../components/MachineDetails/constants';
import { MACHINE_TYPES } from '../../components/MachineList/Constants';
import { MRT_ToggleFullScreenButton, MRT_ToggleDensePaddingButton } from 'material-react-table';


const INITIAL_PAGE_NUMBER = 1;
const INITIAL_PAGE_SIZE = 20;


const TopicSubscriptions = () => {
  const { machineId } = useParams<{ machineId: string }>();

  const [machine, setMachine] = useState<IClient>();
  const [modules, setModules] = useState<any[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState<string>('');
  const [subscribedTopicName, setSubscribedTopicName] = useState<string>('');
  const [pageNumber, setPageNumber] = useState(INITIAL_PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [response, setResponse] = useState<any>();

  const [selectedTopics, setSelectedTopics] = useState<any[]>([]);
  const [isRequesting, setIsRequesting] = useState(false);
  const [erroredApis, setErroredApis] = useState<any[]>([]);

  const dispatch = useDispatch();
  const history = useHistory();
  const { height } = useWindowDimensions();
  const { classes } = useStyles();

  const moduleData = useMemo(() => modules.map(module => ({ ...module, key: module.id, displayName: module.name })), [
    modules
  ]);

  const selectedModule = useMemo(() => moduleData.find(module => module.id === selectedModuleId), [
    moduleData,
    selectedModuleId
  ]);

  useEffect(() => {
    getClientById(machineId)
      .then(machine => {
        setMachine(machine);
        fetchMachineSubscriptions(machine, INITIAL_PAGE_SIZE, INITIAL_PAGE_NUMBER);
      })
      .catch(error => {
        history.replace('/');
        dispatch(displaySnackbar(`${ERROR_MACHINE_NOT_FOUND}: ${error}`, 'error'));
      });
  }, [machineId, history, dispatch]);

  const fetchModuleTopics = useCallback(
    async (module: any, itemsPerPage: number, page: number) => {
      setSelectedModuleId(module ? module.id : '');
      setPageSize(itemsPerPage);
      setPageNumber(page);

      try {
        const response = await getModuleTopics(module, {
          pageNumber: page,
          pageSize: itemsPerPage
        });
        const updatedResponse = response.data.map((topic: any, i: number) => {
          return {
            ...topic,
            id: `${topic.name}-${i}`,
            access: []
          };
        });
        setResponse({
          ...response,
          data: updatedResponse
        });
      } catch (error: any) {
        const errMessage = error.response?.data?.message || error.message;
        dispatch(displaySnackbar(interpolateString([errMessage], ERROR_WHILE_FETCHING_DATA), 'error'));
        setResponse({ data: [], metadata: { totalCount: 0, pageNumber: 1, pageSize: 10 } });
      }
    },
    [dispatch]
  );
  const fetchMachineSubscriptions = useCallback(
    async (machine, itemsPerPage: number, page: number) => {
      if (!machine?.subjectId) return;
      setPageSize(itemsPerPage);
      setPageNumber(page);
      try {
        const response = await getTopicSubscriptions('', machine?.subjectId, {
          pageNumber: page,
          pageSize: itemsPerPage
        });
        const subscriptionName = response.data ? response.data[0]?.name || '' : '';
        setSubscribedTopicName(subscriptionName);
      } catch (error: any) {
        const errMessage = error.response?.data?.message || error.message;
        dispatch(displaySnackbar(interpolateString([errMessage], ERROR_WHILE_FETCHING_SUBSCRIPTIONS), 'error'));
        setSubscribedTopicName('');
      }
    },
    [machine?.subjectId, dispatch]
  );
  useEffect(() => {
        fetchModuleTopics('', INITIAL_PAGE_SIZE, INITIAL_PAGE_NUMBER);
  }, [fetchModuleTopics]);

  const handleBackClick = () => {
    dispatch(setCurrentMachineSelection(machine!));
    dispatch(setCurrentMachineName(machine!.displayName));
    if (history.location.search) {
      history.push(`/machineDetail/${machineId}${history.location.search}`, { fromPage: 'topic-subscriptions' });
    } else {
      history.push('/machineDetail/' + machineId, { fromPage: 'topic-subscriptions' });
    }
  };

  const handleSaveClick = async () => {
    setIsRequesting(true);
    const isSelectedTopicHasAccess = selectedTopics.some(topic => !topic.access.length);
    if (isSelectedTopicHasAccess) {
      dispatch(displaySnackbar(TOPIC_SUBSCRIBE_WARN, 'warning'));
      setIsRequesting(false);
      return;
    }
    const subscriptionTopics = selectedTopics.flatMap(topic => {
      const entries: any = [];
      entries.push({ topicName: topic.name, topicAccess: topic.access[0] });
      return entries;
    });
    try {
      if (subscribedTopicName === '') {
        await addMachineTopicSubscriptions(machine?.subjectId, subscriptionTopics)
          .then()
          .catch(error => {
            throw error;
          });
      } else {
        await updateMachineTopicSubscriptions(machine?.subjectId, subscribedTopicName, subscriptionTopics)
          .then()
          .catch(error => {
            throw error;
          });
      }
      setIsRequesting(false);
      dispatch(displaySnackbar(TOPICS_SUBSCRIBED_SUCCESSFULLY, 'success'));
      dispatch(setMachineSelectedTab(LABELS.TOPICS));
      dispatch(setSelectedTab('Machines'));
      dispatch(setCurrentMachineSelection(machine!));
      dispatch(setCurrentMachineName(machine!.displayName));
      if (history.location.search) {
        history.push(`/machineDetail/${machineId}${history.location.search}`, { fromPage: 'topic-subscriptions' });
      } else {
        history.push(`/machineDetail/${machineId}`, { fromPage: 'topic-subscriptions' });
      }
    } catch (error: any) {
      setIsRequesting(false);
      dispatch(displaySnackbar(interpolateString([error.message], UNSUCCESSFUL_TOPIC_SUBSCRIBE_MESSAGE), 'error'));
    }
  };

  const handleChangePageOptions = (itemsPerPage: number, page: number) => {
    fetchModuleTopics(selectedModule, itemsPerPage, page);
  };

  const handleErroredApisModalClose = () => {
    setErroredApis([]);
  };
  const getMachineSubtitle = () => {
    let subtitle = '';
    if (machine?.displayName) {
      subtitle += `Machine: ${machine.displayName} | `;
    }
    if (machine?.type) {
      subtitle += `Type: ${MACHINE_TYPES[machine.type] || ''}`;
    }
    return subtitle;
  };
  const extendedToolbar = ({ table }) => {
    // const leftComponents = {
    //   component: (
    //     <div>
    //       <LuiSelect
    //         itemData={moduleData}
    //         selectedItem={selectedModule}
    //         placeholder="Select Module"
    //         inputLabel=""
    //         width={DROPDOWN_WIDTH}
    //         maxHeight={DROPDOWN_MAX_HEIGHT}
    //         onSelectionChange={handleModuleSelectionChange}
    //         size="small"
    //       />
    //     </div>
    //   )
    // };
    const rightComponents = {
      component: (
        <div>
          <Typography variant="body1" sx={{ display: 'inline', marginRight: '10px' }}>
            {`Mark selected ${selectedTopics.length > 0 ? `${selectedTopics.length} ` : ''}topics as:`}
          </Typography>
          <Button
            color="primary"
            size="small"
            variant="outlined"
            disabled={selectedTopics.length === 0}
            onClick={handleAssignProducerAccess}
            className={classes.actionButton}
          >
            Producer
          </Button>
          <Button
            color="primary"
            size="small"
            variant="outlined"
            disabled={selectedTopics.length === 0}
            onClick={handleAssignConsumerAccess}
            className={classes.actionButton}
          >
            Consumer
          </Button>
          <Button
            color="secondary"
            size="small"
            disabled={selectedTopics.length === 0}
            onClick={handleSelectionReset}
            sx={{ marginRight: '10px' }}
            className={classes.actionButton}
          >
            Reset
          </Button>
          <MRT_ToggleDensePaddingButton table={table} />
          <MRT_ToggleFullScreenButton table={table} />
        </div>
      )
    };
    return <LuiToolbar className={classes.toolbarPanel} rightZone={rightComponents} />;
  };
  const emptyTableFallback = () => {
    return (
      <Box my={5}>
        <LuiEmptyState imageItem={null} heading={EMPTY_HEADER} />
      </Box>
    );
  };

  const handleAssignProducerAccess = () => {
    const updatedTopics = selectedTopics.map((topic: any) => {
      return { ...topic, access: [PRODUCER] };
    });
    setSelectedTopics(updatedTopics);
  };

  const handleAssignConsumerAccess = () => {
    const updatedTopics = selectedTopics.map((topic: any) => {
      return { ...topic, access: [CONSUMER] };
    });
    setSelectedTopics(updatedTopics);
  };

  const handleSelectionReset = () => {
    setSelectedTopics([]);
  };
  const columns = [
    {
      header: NAME,
      accessorKey: 'name',
      muiTableHeadCellProps: ({ column, table }) =>
        ({
          sx: theme => ({
            ...LuiMaterialTableHeadCellStyles({
              column,
              theme,
              table,
              styling: 'standard',
              virtualizationEnabled: false
            }),
            width: '60%'
          })
        } as TableCellProps)
    },
    {
      header: ACCESS,
      accessorKey: 'access',
      muiTableHeadCellProps: ({ column, table }) =>
        ({
          sx: theme => ({
            ...LuiMaterialTableHeadCellStyles({
              column,
              theme,
              table,
              styling: 'standard',
              virtualizationEnabled: false
            }),
            width: '40%'
          })
        } as TableCellProps),
      Cell: ({ row }) => {
        const isSelected = selectedTopics.find(topic => topic.name === row.original.name);
        return (
          <RadioGroup row value={isSelected ? isSelected.access[0] : ''} onChange={evt => handleAccessChange(evt, row.original)}>
            <FormControlLabel
              value={PRODUCER}
              control={<Radio color="primary" size="small" />}
              label={ACCESS_LABELS.PRODUCER}
            />
            <FormControlLabel
              value={CONSUMER}
              control={<Radio color="primary" size="small" />}
              label={ACCESS_LABELS.CONSUMER}
            />
          </RadioGroup>
        );
      }
    }
  ];
  const handleAccessChange = (event: any, rowData: any) => {
    rowData.access = [event.target.value];
    setSelectedTopics((prevSelectedTopics: any[]) => {
      const isAlreadySelected = prevSelectedTopics.some(topic => topic.name === rowData.name);
      if (isAlreadySelected) {
        return prevSelectedTopics.map(topic => (topic.name === rowData.name ? rowData : topic));
      } else {
        return [...prevSelectedTopics, rowData];
      }
  });
}

  const detailColumns = [
    {
      header: NAME,
      accessorKey: 'name'
    },
    {
      header: ACCESS,
      accessorKey: 'access',
      Cell: ({ row }: any) => {
        return (
          <FormGroup>
            {row.original.access.producer && <FormLabel>Producer</FormLabel>}
            {row.original.access.consumer && <FormLabel>Consumer</FormLabel>}
          </FormGroup>
        );
      }
    }
  ];

  return (
    <Box className={classes.root}>
      {isRequesting && (
        <Backdrop open={true}>
          <CircularProgress color="inherit" size={44} />
        </Backdrop>
      )}
      <LuiPageHeader
        title={ADD_SUBSCRIPTIONS}
        subtitle={getMachineSubtitle()}
        isCollapsable={false}
        onClickBack={handleBackClick}
        toolBarSlot={[
          {
            alwaysVisible: true,
            render: (
              <Button variant="outlined" onClick={handleSaveClick} disabled={selectedTopics.length === 0}>
                {SUBSCRIBE}
              </Button>
            )
          }
        ]}
      ></LuiPageHeader>
      <CustomTablePagination
        startsFromZero={false}
        data={response?.data ?? []}
        loading={!response}
        withoutPagination={!response}
        totalCount={response?.metadata?.totalCount ?? 0}
        columns={columns}
        selectedRowsData={selectedTopics}
        onSelectedRowsDataChange={setSelectedTopics}
        rowsPerPageOptions={[20, 50, 100]}
        labelRowsPerPage={ROWS_PER_PAGE_LABEL}
        currentPageNumber={pageNumber}
        currentItemsPerPage={pageSize}
        onChangePageOptions={handleChangePageOptions}
        maxTableBodyHeight={height - 35 * 8}
        renderTopToolbar={extendedToolbar}
        renderEmptyRowsFallback={emptyTableFallback}
      />
      {erroredApis.length > 0 && (
        <DetailsModal
          openModal={true}
          handleClose={handleErroredApisModalClose}
          dialogTitle={UNSUCCESSFUL_SUBSCRIPTION_TITLE}
        >
          <Typography>{UNSUCCESSFUL_SUBSCRIPTION_MESSAGE}</Typography>
          <LuiMaterialTable
            enableColumnOrdering={false}
            enableColumnsToggle={false}
            enableDensityToggle={false}
            enableColumnPinning={false}
            data={erroredApis}
            keyAccessor="id"
            columns={detailColumns}
          />
        </DetailsModal>
      )}
      <SnackbarAlert />
    </Box>
  );
};

export default TopicSubscriptions;
